import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { displayNameOf, useAuth } from '../auth/AuthProvider';
import { atLeast } from '../queries/orgs';
import { Wordmark } from './Clapper';
import { AppLink, usePathname } from './Nav';
import { ChevronDownIcon, GearIcon, LinkIcon, PlusIcon, RecordIcon } from './icons';
import { Button, cx } from './ui';

/**
 * The signed-in chrome: identity, workspace, and the few places you can go.
 * Deliberately shallow — Slate has one job and the nav should not imply
 * otherwise.
 */

const MENU_ITEM =
  'flex w-full items-center gap-2 rounded px-2.5 py-2 text-left text-body text-chalk-dim ' +
  'transition-colors duration-120 hover:bg-slate-800 hover:text-chalk';

function useDismissable(open: boolean, close: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [close, open]);
  return containerRef;
}

function NavLink({
  to,
  params,
  children,
}: {
  to: string;
  params?: Record<string, string>;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const resolved = params
    ? Object.entries(params).reduce((path, [key, value]) => path.replace(`$${key}`, value), to)
    : to;
  const active = resolved === '/' ? pathname === '/' : pathname === resolved;

  return (
    <AppLink
      to={to}
      params={params}
      className={cx(
        'rounded px-2.5 py-1.5 text-body transition-colors duration-120',
        active ? 'bg-slate-800 text-chalk' : 'text-chalk-dim hover:bg-slate-850 hover:text-chalk',
      )}
    >
      {children}
    </AppLink>
  );
}

function WorkspaceSwitcher() {
  const { orgs, activeOrg, select } = useWorkspace();
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable(open, () => setOpen(false));

  if (orgs.length === 0) {
    return (
      <AppLink
        to="/org/new"
        className="hidden items-center gap-1.5 rounded border border-hairline px-2.5 py-1.5 text-caption text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk sm:flex"
      >
        <PlusIcon size={13} />
        Create a team
      </AppLink>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded border border-hairline bg-slate-850 px-2.5 py-1.5 text-body text-chalk transition-colors duration-120 hover:border-slate-600"
      >
        <span className="max-w-[10rem] truncate">{activeOrg?.name ?? 'Personal'}</span>
        <ChevronDownIcon size={14} className="text-chalk-faint" />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-64 animate-fade-up rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          <button
            type="button"
            onClick={() => {
              select(null);
              setOpen(false);
            }}
            className={cx(
              'flex w-full flex-col items-start rounded px-2.5 py-2 text-left transition-colors duration-120 hover:bg-slate-800',
              !activeOrg && 'bg-slate-800',
            )}
          >
            <span className="text-body text-chalk">Personal</span>
            <span className="text-caption text-chalk-faint">Just yours. Free forever.</span>
          </button>

          {orgs.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => {
                select(org.id);
                setOpen(false);
              }}
              className={cx(
                'flex w-full flex-col items-start rounded px-2.5 py-2 text-left transition-colors duration-120 hover:bg-slate-800',
                activeOrg?.id === org.id && 'bg-slate-800',
              )}
            >
              <span className="text-body text-chalk">{org.name}</span>
              <span className="text-caption capitalize text-chalk-faint">{org.role}</span>
            </button>
          ))}

          <div className="my-1 h-px bg-hairline" />
          <AppLink to="/org/new" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <PlusIcon size={14} />
            New organization
          </AppLink>
        </div>
      ) : null}
    </div>
  );
}

function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useDismissable(open, () => setOpen(false));
  const name = displayNameOf(user);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption font-medium uppercase text-chalk-dim transition-colors duration-120 hover:border-slate-600 hover:text-chalk"
      >
        {name.slice(0, 1)}
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1.5 w-56 animate-fade-up rounded-md border border-hairline bg-slate-850 p-1 shadow-raised"
        >
          <div className="px-2.5 py-2">
            <p className="truncate text-body text-chalk">{name}</p>
            <p className="truncate text-caption text-chalk-faint">{user?.email}</p>
          </div>
          <div className="my-1 h-px bg-hairline" />
          <AppLink to="/settings" onClick={() => setOpen(false)} className={MENU_ITEM}>
            <GearIcon size={14} />
            Settings
          </AppLink>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className={MENU_ITEM}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { activeOrg, activeRole } = useWorkspace();

  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="sticky top-0 z-20 border-b border-hairline bg-slate-950/95 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4 sm:px-6">
          <AppLink to="/" className="mr-1" aria-label="Slate home">
            <Wordmark />
          </AppLink>

          <nav className="hidden items-center gap-0.5 md:flex">
            <NavLink to="/">Library</NavLink>
            <NavLink to="/links">Requests</NavLink>
            {activeOrg ? (
              <>
                <NavLink to="/org/$slug" params={{ slug: activeOrg.slug }}>
                  {activeOrg.name}
                </NavLink>
                {atLeast(activeRole ?? undefined, 'admin') ? (
                  <NavLink to="/org/$slug/members" params={{ slug: activeOrg.slug }}>
                    Members
                  </NavLink>
                ) : null}
                {atLeast(activeRole ?? undefined, 'owner') ? (
                  <NavLink to="/org/$slug/billing" params={{ slug: activeOrg.slug }}>
                    Billing
                  </NavLink>
                ) : null}
              </>
            ) : null}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <WorkspaceSwitcher />
            <AppLink to="/new">
              <Button variant="primary" size="md" className="gap-2">
                <RecordIcon size={11} className="text-record" />
                Record
              </Button>
            </AppLink>
            <AccountMenu />
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6">{children}</main>

      <footer className="border-t border-hairline">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-5 text-caption text-chalk-faint sm:px-6">
          <span>Slate</span>
          <AppLink to="/links" className="flex items-center gap-1.5 hover:text-chalk-dim">
            <LinkIcon size={13} />
            Ask someone for a video
          </AppLink>
          <span className="ml-auto">Built by MetaCTO</span>
        </div>
      </footer>
    </div>
  );
}
